/**
 * R6 §5 / D-12, generalized by R7 WS-0 §4 — "where does this principal land?", in ONE place.
 *
 * ⛔ ONE FUNCTION, ONE ANSWER. The old shell shipped two disagreeing answers to this question
 * — `SectionHeader` used the first visible entry while `AppHost` hardcoded
 * `/platform/tenants` — and FIVE of seven principal shapes landed on an empty pane. That is
 * why `landing.ts` exists at all, and it must stay the single answer.
 *
 * ⛔ It keeps its R6 property: `resolveLanding` takes the SERVER-FILTERED app list, never grants
 * (D-11). `resourceGrants` is a client snapshot that may be null or truncated; `GET /v1/apps` is
 * enforced by `@FilterByResourcePermission` on release-engine. Landing on a grant-derived guess
 * that the server would 403 is the failure the argument type prevents.
 */
import type { App } from '../api/apps.api';
import {
  GLOBAL_ENTRIES,
  SECTION_ORDER,
  onRail,
  type AppScopedEntry,
  type GlobalEntry,
  type ShellSection,
} from './manifest';
import { isPathUnder } from './path-under';

/**
 * A section the principal can land on, in IA order. Built from the visible registry.
 *
 * Two shapes keep `resolveLanding` free of an unreachable arm. A non-Apps section always has a
 * global entry to land on, so its candidate carries its `to`. The app list candidate carries no
 * `to`: its destination depends on the app list, which arrives later. An Apps entry routed outside
 * the app list (Kernel rights, Library rights) is a `global` candidate with section `apps`.
 */
export type LandingCandidate =
  | { readonly kind: 'global'; readonly section: ShellSection; readonly to: string }
  | { readonly kind: 'apps' };

export type Landing =
  | { readonly kind: 'section'; readonly to: string }
  | { readonly kind: 'app'; readonly appId: string } // exactly one visible app
  | { readonly kind: 'catalog' } // two or more
  | { readonly kind: 'apps-pending' } // Apps leads and its list is still unknown
  | { readonly kind: 'none' }; // nothing visible anywhere

/**
 * Every section this principal can land on, in `SECTION_ORDER` — i.e. IA order, not entry order.
 *
 * ⚠️ `find` returns the FIRST match in `GLOBAL_ENTRIES` declaration order, so manifest composition
 * order decides a section's landing screen, the same order the rail draws its items in. A
 * workstream declaring `Tenants` before `Data planes` is the whole mechanism by which Platform
 * lands on Tenants.
 *
 * The candidates from rail entries come first, so a `navHidden` entry never takes the landing from
 * a screen the rail shows. A section that holds only hidden entries comes after them. So a
 * principal whose one screen is off the rail still reaches it, and resolveLanding falls through
 * to it when an earlier candidate, such as an empty Apps list, cannot land.
 */
export function buildLandingCandidates(
  visibleGlobal: readonly GlobalEntry[],
  visibleAppScoped: readonly AppScopedEntry[],
): readonly LandingCandidate[] {
  const onRailCandidates = candidatesFrom(visibleGlobal.filter(onRail), visibleAppScoped);
  const hiddenOnly = candidatesFrom(visibleGlobal, visibleAppScoped).filter(
    (c) => !onRailCandidates.some((r) => slotOf(r) === slotOf(c)),
  );
  return [...onRailCandidates, ...hiddenOnly];
}

/** The slot a candidate fills. The app list and an Apps entry outside it fill different slots. */
function slotOf(candidate: LandingCandidate): ShellSection | 'app-list' {
  return candidate.kind === 'apps' ? 'app-list' : candidate.section;
}

/**
 * Kernel rights and Library rights sat last in Access before they moved to Apps, and plan ruling R3
 * keeps that landing. So an Apps entry routed outside the app list takes the empty Access slot.
 */
const OFF_LIST_APPS_LANDING_SLOT: ShellSection = 'access';

/**
 * One candidate per section that `entries` populates, in `SECTION_ORDER`, plus the first off-list
 * Apps entry in the empty Access slot. An Access candidate always lands, so that entry adds nothing then.
 */
function candidatesFrom(
  visibleGlobal: readonly GlobalEntry[],
  visibleAppScoped: readonly AppScopedEntry[],
): readonly LandingCandidate[] {
  // The app list lands on `/apps` or `/apps/:id`; any other Apps entry lands on its own route.
  const onList: GlobalEntry[] = [];
  const offList: GlobalEntry[] = [];
  for (const e of visibleGlobal) {
    if (e.section === 'apps') (isPathUnder(e.routePrefix, '/apps') ? onList : offList).push(e);
  }
  const [firstOffList] = offList;
  const out: LandingCandidate[] = [];
  for (const section of SECTION_ORDER) {
    if (section === 'apps') {
      // Visible through EITHER kind: a principal holding only `cp:app:runtime:read` on one app
      // sees Fleet but NOT the catalog (`entryAllows`'s grant leg is exact-match), so a
      // global-only test would hide the Apps section from exactly the population R6 shipped for.
      if (onList.length > 0 || visibleAppScoped.length > 0) out.push({ kind: 'apps' });
      continue;
    }
    const first = visibleGlobal.find((e) => e.section === section);
    if (first !== undefined) {
      out.push({ kind: 'global', section, to: first.routePrefix });
    } else if (section === OFF_LIST_APPS_LANDING_SLOT && firstOffList !== undefined) {
      out.push({ kind: 'global', section: 'apps', to: firstOffList.routePrefix });
    }
  }
  return out;
}

/**
 * Where this principal lands, given the candidates and the SERVER-FILTERED app list (D-11).
 *
 * ⛔ `apps === null` means the `GET /v1/apps` round-trip is still in flight (`apps-context.tsx`),
 * and it must NOT be collapsed to `[]` — that is the exact inversion the `AppsListState` docblock in
 * `apps-context.tsx` forbids, and it would turn a slow fetch into a denial.
 */
export function resolveLanding(
  candidates: readonly LandingCandidate[],
  apps: readonly App[] | null,
): Landing {
  for (const c of candidates) {
    if (c.kind === 'global') return { kind: 'section', to: c.to };
    // The Apps section. Its destination is a function of the LIST.
    if (apps === null) return { kind: 'apps-pending' };
    // Destructuring rather than `.length` checks: it narrows `only` to `App` on the single-app
    // path, so there is no assertion and no invented fallback.
    const [only, second] = apps;
    if (second) return { kind: 'catalog' };
    if (only) return { kind: 'app', appId: only.id };
    // Loaded and genuinely empty ⇒ Apps has nothing to land on. Fall through to the next
    // candidate rather than dead-ending: a principal with `cp:app:read` site-wide, zero apps and
    // `cp:role:read` should land in Access, not on a denial.
  }
  return { kind: 'none' };
}

/**
 * For a principal that holds `cp:app:create` site-wide, the Apps slot never falls through. Pure.
 * Caller: `AuthenticatedShell`, on the answer of {@link resolveLanding}.
 *
 * - A non-creator gets `landing` unchanged.
 * - A creator with `none` gets the catalogue, which holds the New app door.
 * - A creator whose `section` landing is a global entry of a section after `apps` in
 *   `SECTION_ORDER` gets the catalogue. `resolveLanding` reaches such a section only when the
 *   shell loaded the app list and the list is empty.
 * - A creator with any other landing gets it unchanged:
 *   - a section before `apps`;
 *   - an Apps entry outside the app list;
 *   - one app;
 *   - the catalogue;
 *   - the pending list.
 *
 * The site `app_developer` (D4 of 2026-10-20-cp-permissioning) holds `cp:app:create`. Since
 * 2026-10-26-appdev-permissions A-001 it also holds `cp:channel:private:read`. Before its first app
 * the empty list falls through to Private channels in `releases`. Decision 5 of that plan keeps it
 * on the catalogue.
 */
export function withCreateDoor(landing: Landing, canCreateApp: boolean): Landing {
  if (!canCreateApp) return landing;
  if (landing.kind === 'none') return { kind: 'catalog' };
  if (landing.kind === 'section' && sectionIsAfterApps(landing.to)) return { kind: 'catalog' };
  return landing;
}

/** True when `to` is the route of a global entry whose section sorts after `apps` in `SECTION_ORDER`. */
function sectionIsAfterApps(to: string): boolean {
  const entry = GLOBAL_ENTRIES.find((e) => e.routePrefix === to);
  return entry !== undefined && SECTION_ORDER.indexOf(entry.section) > SECTION_ORDER.indexOf('apps');
}

/**
 * The router path for a landing.
 *
 * ⛔ ROUTER-RELATIVE, always — i.e. what `<BrowserRouter>` matches, not what the browser shows.
 * The two coincide at `BASENAME = ''`, so a hand-written prefix here would go UNNOTICED today and
 * double the moment a basename is reintroduced (`/x/apps` → `/x/x/apps`). Write the router path.
 *
 * ⚠️ The URL segment for an app is the `appId` (uuid), NOT the slug — FORCED, not chosen
 * (D-19). Every app-scoped release-engine route is
 * `@RequireResourcePermission('cp:app:read', 'id'|'appId')` and `cp_auth.resource_grants`
 * stores `resource_id` as the app uuid, so a slug in the path would not resolve against the
 * guard. The slug IS displayed — in the rail's app-scope block and the header chip — so the
 * human-readable identifier is present without being the routing key.
 */
export function landingPath(landing: Landing): string {
  switch (landing.kind) {
    case 'section':
      return landing.to;
    case 'app':
      return `/apps/${landing.appId}`;
    case 'catalog':
      return '/apps';
    case 'apps-pending':
      return '/';
    case 'none':
      return '/';
  }
}
